import { FormEvent, useMemo, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { submitLead } from '../../api/leads';
import posthog from 'posthog-js';
import { env } from '../../lib/env';
import { logClientError } from '../../lib/telemetry';

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
        },
      ) => string;
      reset?: (widgetId?: string) => void;
    };
  }
}

type TurnstileWidgetProps = {
  siteKey: string;
  onToken: (token: string | null) => void;
  onExpired?: () => void;
  onError?: () => void;
};

function TurnstileWidget({ siteKey, onToken, onExpired, onError }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const tokenHandlerRef = useRef(onToken);
  const expiredHandlerRef = useRef(onExpired);
  const errorHandlerRef = useRef(onError);

  useEffect(() => {
    tokenHandlerRef.current = onToken;
  }, [onToken]);

  useEffect(() => {
    expiredHandlerRef.current = onExpired;
  }, [onExpired]);

  useEffect(() => {
    errorHandlerRef.current = onError;
  }, [onError]);

  useEffect(() => {
    let scriptElement: HTMLScriptElement | null = null;
    let loadHandler: (() => void) | null = null;
    const initialContainer = containerRef.current;
    const renderWidget = () => {
      if (!window.turnstile || !containerRef.current) return;
      // Ensure the container only holds a single widget instance.
      containerRef.current.innerHTML = '';
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => tokenHandlerRef.current?.(token),
        'error-callback': () => {
          tokenHandlerRef.current?.(null);
          errorHandlerRef.current?.();
        },
        'expired-callback': () => {
          tokenHandlerRef.current?.(null);
          expiredHandlerRef.current?.();
        },
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      const existingScript = document.querySelector<HTMLScriptElement>('script[data-turnstile-script]');
      loadHandler = () => renderWidget();
      if (existingScript) {
        existingScript.addEventListener('load', loadHandler, { once: true });
      } else {
        scriptElement = document.createElement('script');
        scriptElement.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        scriptElement.async = true;
        scriptElement.defer = true;
        scriptElement.setAttribute('data-turnstile-script', 'true');
        scriptElement.addEventListener('load', loadHandler, { once: true });
        document.body.appendChild(scriptElement);
      }
    }

    return () => {
      if (widgetIdRef.current && window.turnstile?.reset) {
        window.turnstile.reset(widgetIdRef.current);
      }
      widgetIdRef.current = null;
      if (initialContainer) {
        initialContainer.innerHTML = '';
      }
      if (loadHandler) {
        const script = document.querySelector<HTMLScriptElement>('script[data-turnstile-script]') ?? scriptElement;
        script?.removeEventListener('load', loadHandler);
      }
    };
  }, [siteKey]);

  return <div ref={containerRef} className="min-h-[70px]" />;
}

export function ContactPage() {
  const CONTACT_DRAFT_STORAGE_KEY = 'contact_lead_draft';
  const hasHydratedDraftRef = useRef(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [topic, setTopic] = useState('general');
  const [consent, setConsent] = useState(false);
  const [devVerification, setDevVerification] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileRefreshKey, setTurnstileRefreshKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [honeypotValue, setHoneypotValue] = useState('');
  const turnstileSiteKey = env.turnstileSiteKey;
  const devTurnstileToken = useMemo(() => (turnstileSiteKey ? null : 'dev-turnstile-token'), [turnstileSiteKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || hasHydratedDraftRef.current) return;
    try {
      const draftRaw = window.localStorage.getItem(CONTACT_DRAFT_STORAGE_KEY);
      if (draftRaw) {
        const draft = JSON.parse(draftRaw) as Partial<{
          name: string;
          email: string;
          company: string;
          message: string;
          topic: string;
          consent: boolean;
        }>;
        if (draft.name) setName(draft.name);
        if (draft.email) setEmail(draft.email);
        if (draft.company) setCompany(draft.company);
        if (draft.message) setMessage(draft.message);
        if (draft.topic) setTopic(draft.topic);
        if (typeof draft.consent === 'boolean') setConsent(draft.consent);
      }
    } catch (draftError) {
      console.warn('[contact] Failed to hydrate draft', draftError);
    } finally {
      hasHydratedDraftRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !hasHydratedDraftRef.current) return;
    const trimmedFields = [name, email, company, message].map((value) => value.trim());
    const hasDraftContent = trimmedFields.some((value) => value.length > 0) || topic !== 'general' || consent;
    if (!hasDraftContent) {
      window.localStorage.removeItem(CONTACT_DRAFT_STORAGE_KEY);
      return;
    }
    const draftPayload = {
      name,
      email,
      company,
      message,
      topic,
      consent,
      updatedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(CONTACT_DRAFT_STORAGE_KEY, JSON.stringify(draftPayload));
  }, [company, consent, email, message, name, topic]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const leadEventPayload = {
      topic,
      hasCompany: Boolean(company),
      messageLength: message.trim().length,
      turnstileConfigured: Boolean(turnstileSiteKey),
    };
    if (honeypotValue.trim().length > 0) {
      posthog.capture('contact_lead_blocked', { ...leadEventPayload, reason: 'honeypot_triggered' });
      logClientError({
        source: 'contact_form',
        message: 'Honeypot triggered',
        data: leadEventPayload,
      });
      return;
    }
    posthog.capture('contact_lead_submit_attempt', leadEventPayload);
    if (!consent) {
      const consentMessage = 'Please agree to be contacted by the Lighthouse team.';
      setError(consentMessage);
      posthog.capture('contact_lead_blocked', { ...leadEventPayload, reason: 'consent_missing' });
      return;
    }
    if (!turnstileSiteKey && !devVerification) {
      const verificationMessage = 'Please confirm you are not a robot.';
      setError(verificationMessage);
      posthog.capture('contact_lead_blocked', { ...leadEventPayload, reason: 'dev_verification_missing' });
      return;
    }
    if (turnstileSiteKey && !turnstileToken) {
      const verificationMessage = 'Please complete the Turnstile verification.';
      setError(verificationMessage);
      posthog.capture('contact_lead_blocked', { ...leadEventPayload, reason: 'turnstile_missing' });
      return;
    }
    setError(null);
    setSubmitting(true);

    try {
      await submitLead({
        name,
        email,
        company: company || undefined,
        message,
        topic,
        turnstileToken: turnstileSiteKey ? turnstileToken : devTurnstileToken,
      });
      toast.success('Thanks for reaching out. A Lighthouse advocate will reply within 1 business day.');
      posthog.capture('contact_lead_submit_success', leadEventPayload);
      setName('');
      setEmail('');
      setCompany('');
      setMessage('');
      setTopic('general');
      setConsent(false);
      setDevVerification(false);
      setTurnstileToken(null);
      setHoneypotValue('');
      if (turnstileSiteKey) {
        setTurnstileRefreshKey((value) => value + 1);
      }
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(CONTACT_DRAFT_STORAGE_KEY);
      }
    } catch (leadError) {
      const messageText =
        leadError instanceof Error ? leadError.message : 'We could not submit your message. Please try again.';
      toast.error(messageText);
      setError(messageText);
      posthog.capture('contact_lead_submit_error', { ...leadEventPayload, messageText });
      logClientError({
        source: 'contact_form',
        message: messageText,
        error: leadError,
        data: { topic, email, company, turnstileConfigured: Boolean(turnstileSiteKey) },
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleTurnstileExpired = () => {
    posthog.capture('contact_lead_turnstile_expired');
    setTurnstileToken(null);
    setTurnstileRefreshKey((value) => value + 1);
  };

  const handleTurnstileError = () => {
    posthog.capture('contact_lead_turnstile_error');
    setTurnstileToken(null);
  };

  return (
    <main className="min-h-screen bg-[#090b1a] pb-24" aria-labelledby="contact-heading">
      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <header className="space-y-6 text-center">
          <Badge className="mx-auto w-fit bg-gold-gradient text-black shadow-lg shadow-[#d2ac47]/35">
            Let’s collaborate
          </Badge>
          <h1 id="contact-heading" className="text-4xl font-semibold text-white sm:text-5xl">
            Talk to Lighthouse about inclusive hiring
          </h1>
          <p className="text-lg text-gray-300">
            Whether you’re returning to work or building programmes for returners, we’re here to help.
          </p>
        </header>

        <div className="mt-12 grid gap-8 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <form onSubmit={handleSubmit} className="h-full">
            <Card className="border-white/10 bg-white/[0.03]">
              <CardHeader>
                <CardTitle className="text-xl text-white">Send a message</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
              {error && <p className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">{error}</p>}
              <div className="space-y-2">
                <label htmlFor="contact-name" className="text-sm text-gray-300">
                  Name
                </label>
                <Input
                  id="contact-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                  placeholder="How should we address you?"
                  className="border-white/10 bg-white/10 text-white placeholder:text-gray-400"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="contact-email" className="text-sm text-gray-300">
                  Email
                </label>
                <Input
                  id="contact-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  placeholder="you@example.com"
                  className="border-white/10 bg-white/10 text-white placeholder:text-gray-400"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="contact-company" className="text-sm text-gray-300">
                  Company or role
                </label>
                <Input
                  id="contact-company"
                  value={company}
                  onChange={(event) => setCompany(event.target.value)}
                  placeholder="Optional"
                  className="border-white/10 bg-white/10 text-white placeholder:text-gray-400"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="contact-topic" className="text-sm text-gray-300">
                  How can we help?
                </label>
                <select
                  id="contact-topic"
                  value={topic}
                  onChange={(event) => setTopic(event.target.value)}
                  className="w-full rounded-md border border-white/10 bg-white/10 px-3 py-2 text-sm text-white outline-none focus-visible:border-[#f7ef8a]"
                >
                  <option value="general">General enquiry</option>
                  <option value="returner-programmes">Returner programme design</option>
                  <option value="inclusive-audit">Inclusive job spec audit</option>
                  <option value="platform-demo">Product demo / pricing</option>
                </select>
              </div>
              <div className="space-y-2">
                <label htmlFor="contact-message" className="text-sm text-gray-300">
                  Message
                </label>
                <Textarea
                  id="contact-message"
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  required
                  rows={5}
                  placeholder="Tell us how Lighthouse can help. The more detail the better!"
                  className="border-white/10 bg-white/10 text-white placeholder:text-gray-400"
                />
              </div>
              {/* Hidden honeypot field to block basic spambots */}
              <div className="hidden" aria-hidden="true">
                <label htmlFor="contact-website">Company website</label>
                <Input
                  id="contact-website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypotValue}
                  onChange={(event) => setHoneypotValue(event.target.value)}
                />
              </div>
              {!turnstileSiteKey && (
                <label className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 p-3 text-sm text-gray-200">
                  <Checkbox checked={devVerification} onCheckedChange={(checked) => setDevVerification(Boolean(checked))} />
                  I&apos;m not a robot (dev verification)
                </label>
              )}
              {turnstileSiteKey && (
                <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                  <TurnstileWidget
                    key={turnstileRefreshKey}
                    siteKey={turnstileSiteKey}
                    onToken={setTurnstileToken}
                    onExpired={handleTurnstileExpired}
                    onError={handleTurnstileError}
                  />
                </div>
              )}
              <label className="flex items-start gap-2 text-sm text-gray-300">
                <Checkbox checked={consent} onCheckedChange={(checked) => setConsent(Boolean(checked))} />
                <span>
                  I agree to be contacted by Lighthouse about this request. Read more in our{' '}
                  <Link className="underline" to="/privacy-policy">
                    privacy policy
                  </Link>
                  .
                </span>
              </label>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-gold-gradient text-black hover:brightness-110"
              >
                {submitting ? 'Sending...' : 'Submit'}
              </Button>
              </CardContent>
            </Card>
          </form>

          <aside aria-label="Contact options and topics">
            <Card className="border-white/10 bg-white/[0.02]">
              <CardContent className="space-y-6 p-6">
                <div>
                  <h2 className="text-lg font-semibold text-white">Popular topics</h2>
                  <ul className="mt-3 space-y-3 text-sm text-gray-300">
                    <li>• Returner programme design and success metrics</li>
                    <li>• Inclusive job spec audits covered by Lighthouse partners</li>
                    <li>• AI safeguards and moderation guidance</li>
                    <li>• Support for large-scale redeployment or upskilling</li>
                  </ul>
                </div>
                <div className="rounded-2xl border border-[#d2ac47]/30 bg-[#ba9036]/10 p-4 text-sm text-[#f7ef8a]">
                  Prefer async? Email <a href="mailto:hello@lighthouse.jobs" className="underline">hello@lighthouse.jobs</a> or
                  book time with our team to run through the platform and best practices.
                </div>
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-[0.25em] text-gray-400">Office hours</h3>
                  <p className="mt-2 text-sm text-gray-300">
                    Monday to Friday · 09:00 – 17:30 UK time <br />
                    Virtual appointments available worldwide.
                  </p>
                </div>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </main>
  );
}
